import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/api";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ShieldCheck, Clock, Layers, Diamond,
  PencilRuler, ReceiptText, Gem, Lock, FileCheck, MessagesSquare,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Seo, { FAQ_JSONLD, ORGANIZATION_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";
import { responsiveImage } from "@/lib/img";

const HERO = "https://images.pexels.com/photos/11504786/pexels-photo-11504786.jpeg";
const WORKSHOP = "https://images.unsplash.com/photo-1608112169461-48616144c894?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxqZXdlbHJ5JTIwd29ya3Nob3AlMjB0b29sfGVufDB8fHx8MTc4MjgwNzA0NHww&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { icon: ShieldCheck, title: "Verified specialists", body: "A small, hand-picked circle of jewellery CAD designers, vetted on craft." },
  { icon: Lock, title: "Discreet by design", body: "Your brief stays private. Clients and designers never see each other." },
  { icon: FileCheck, title: "Production-ready files", body: "Clean STL, STEP and OBJ, prepared to the deliverables you agreed." },
  { icon: ReceiptText, title: "One fixed price", body: "A transparent quotation per project. No hourly surprises." },
];

const PROCESS = [
  { icon: PencilRuler, title: "Submit a brief", body: "Pick a category, share references, describe your vision. Quotation is free." },
  { icon: ReceiptText, title: "Approve & pay", body: "Receive a fixed-price quotation. Work begins only once you approve it." },
  { icon: Gem, title: "Preview, revise, own", body: "Preview submissions, use your included revisions, then download files that are yours forever." },
];

const DELIVERABLES = [
  { k: "STL", v: "Casting & 3D print" },
  { k: "STEP", v: "Editable solid CAD" },
  { k: "OBJ", v: "Renders & preview" },
];

const FAQS = [
  { q: "How much does a design cost?", a: "Each project is quoted individually based on the category, complexity and your requirements. Submitting a brief and receiving a quotation is free — you only pay once you approve it." },
  { q: "How long does it take?", a: "Most categories deliver an initial draft within 5–7 days. Your quotation shows the estimated turnaround before you commit." },
  { q: "What files do I receive?", a: "Production-ready files as agreed in your quotation — typically STL for casting or printing, with STEP and OBJ available where relevant. Approved files are yours to keep." },
  { q: "How many revisions are included?", a: "Every quotation includes a set number of revisions covering reasonable changes to the approved brief. Larger scope changes can be quoted as an add-on." },
  { q: "Who actually does the work?", a: "A verified specialist working under GetJewelDesigns' Design Team programme. You deal only with the studio, and the studio stands behind every delivery." },
  { q: "Is my idea kept confidential?", a: "Yes. Briefs, messages and files are routed and moderated so your identity and concept stay private throughout." },
];

// Hero entrance animates position only, never opacity. These five elements are
// above the fold and are baked into the prerendered HTML, so an `opacity: 0`
// initial state shipped them invisible until Framer Motion hydrated — Chrome
// does not count an element as painted for LCP until it is actually visible, so
// the H1 (the homepage's LCP element) was gated behind the JS bundle for its
// whole download-parse-execute cycle. Sliding from a visible resting state keeps
// the entrance while letting the text paint on first frame. Sections further
// down still use whileInView with opacity, which is fine: they are below the
// fold and cannot be the LCP element.
export default function Landing() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <Seo
        title="Jewellery CAD design service for jewellers"
        description="Jewellery CAD design service for jewellers. Send a sketch, photo or sample and get production-ready files for rings, pendants, earrings and more at one fixed price."
        path="/"
        jsonLd={[ORGANIZATION_JSONLD, WEBSITE_JSONLD, FAQ_JSONLD(FAQS)]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b hairline">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-8 px-4 md:px-8 py-16 md:py-32">
          <div className="col-span-12 md:col-span-6 flex flex-col justify-center">
            <motion.div
              initial={{ y: 20 }} animate={{ y: 0 }} transition={{ duration: 0.8 }}
              className="text-xs uppercase tracking-[0.3em] text-primary mb-6"
            >
              Managed Jewellery CAD Studio
            </motion.div>
            <motion.h1
              initial={{ y: 20 }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.1 }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] mb-8 tracking-tight"
            >
              Fine jewellery <em className="text-primary not-italic">CAD</em>, crafted by our discreet Design Team.
            </motion.h1>
            <motion.p
              initial={{ y: 20 }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.25 }}
              className="text-muted-foreground max-w-lg text-base mb-10 leading-relaxed"
            >
              GetJewelDesigns is a managed studio for fine jewellery CAD. Submit a reference. Receive a curated quotation. Approve — and watch your piece come to life as a production-ready STL, delivered by a hand-picked specialist under GetJewelDesigns' Design Team.
            </motion.p>
            <motion.div
              initial={{ y: 20 }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.4 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/categories"
                data-testid="hero-cta-categories"
                className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              >
                Explore categories <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-cta-how"
                className="inline-flex items-center gap-3 border hairline px-8 py-4 text-xs uppercase tracking-widest hover:border-primary/60 transition-colors duration-300"
              >
                How it works
              </Link>
            </motion.div>

            <div className="mt-16 flex flex-wrap gap-10 text-xs uppercase tracking-widest text-muted-foreground">
              <div className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-primary" strokeWidth={1} /> Verified specialists</div>
              <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-primary" strokeWidth={1} /> 5–7 day turnaround</div>
              <div className="flex items-center gap-3"><Layers className="h-4 w-4 text-primary" strokeWidth={1} /> STL · STEP · OBJ</div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 relative">
            <motion.div
              initial={{ scale: 1.02 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }}
              className="relative aspect-[3/4] overflow-hidden border hairline"
            >
              <img {...responsiveImage(HERO, { width: 1200, ratio: "3/4", sizes: "(min-width: 768px) 45vw, 100vw" })} alt="Fine jewellery" fetchPriority="high" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="pill pill-gold"><Diamond className="h-3 w-3" strokeWidth={1} /> GetJewelDesigns Studio</div>
                <div className="mt-3 font-serif text-2xl">Precision at every facet.</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border hairline">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-background p-8"
              >
                <v.icon className="h-6 w-6 text-primary mb-5" strokeWidth={1} />
                {/* First level under the hero h1 — h3 here skipped a level. */}
                <h2 className="font-serif text-xl mb-2">{v.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">The Collections</div>
              <h2 className="font-serif text-3xl md:text-5xl">Choose your category.</h2>
            </div>
            <Link to="/categories" data-testid="section-cats-all" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300">View all</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {cats.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link to={`/categories/${c.slug}`} data-testid={`cat-card-${c.slug}`} className="group block border hairline overflow-hidden hover:border-primary/60 transition-colors duration-300">
                  <div className="aspect-[3/4] overflow-hidden bg-muted">
                    <img {...responsiveImage(c.cover_image, { width: 800, ratio: "3/4", sizes: "(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw" })} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-[0.25em] text-primary mb-2">Starting from {fmtMoney(c.starting_price, c.currency)}{c.starting_price_usd ? ` · ${fmtMoney(c.starting_price_usd, "USD")}` : ""}</div>
                    <h3 className="font-serif text-2xl mb-2">{c.name}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{c.short_desc}</p>
                    <div className="mt-4 flex justify-between text-xs uppercase tracking-widest text-muted-foreground">
                      <span>{c.turnaround_days} day{c.turnaround_days > 1 ? "s" : ""}</span>
                      <span>{c.included_revisions} revisions</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works (condensed) */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">The Process</div>
              <h2 className="font-serif text-3xl md:text-5xl">From brief to STL.</h2>
            </div>
            <Link to="/how-it-works" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300">See full process</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PROCESS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="border hairline p-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <s.icon className="h-6 w-6 text-primary" strokeWidth={1} />
                  <span className="font-serif text-4xl text-primary/30">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What you receive */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-8 md:gap-12 px-4 md:px-8 py-16 md:py-24 items-center">
          <div className="col-span-12 md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">Deliverables</div>
            <h2 className="font-serif text-3xl md:text-5xl mb-6">Files built for production.</h2>
            <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">
              Every approved project is delivered as clean, manufacturable geometry — prepared to the exact
              deliverables set in your quotation, and yours to keep forever.
            </p>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <MessagesSquare className="h-5 w-5 text-primary" strokeWidth={1} />
              Chat with the Design Team throughout, all inside the platform.
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border border hairline">
              {DELIVERABLES.map((d) => (
                <div key={d.k} className="bg-background p-8 text-center">
                  <div className="font-serif text-3xl text-primary mb-2">{d.k}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{d.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Freelancer teaser */}
      <section className="border-b hairline">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 md:gap-8 px-4 md:px-8 py-16 md:py-24 items-center">
          <div className="col-span-12 md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border hairline">
              <img {...responsiveImage(WORKSHOP, { width: 1200, ratio: "4/5", sizes: "(min-width: 768px) 50vw, 100vw" })} alt="Workshop" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">For Designers</div>
            <h2 className="font-serif text-3xl md:text-5xl mb-6">Design with GetJewelDesigns.</h2>
            <p className="text-muted-foreground max-w-xl mb-10 leading-relaxed">
              Join a small, hand-selected circle of jewellery CAD specialists. We source the briefs, manage the client, and pay per approved project. Your identity stays private; your craft speaks for itself.
            </p>
            <Link to="/freelancer/apply" data-testid="freelancer-cta" className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Apply as a designer <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b hairline">
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4 text-center">Questions</div>
          <h2 className="font-serif text-3xl md:text-5xl mb-10 md:mb-14 text-center">Good to know.</h2>
          {/* Every answer starts open. Radix only renders a closed item's
              content as an empty, `hidden` div, so with the items collapsed
              the prerendered HTML carried six questions and no answers —
              crawlers and answer engines (which do not run JS or click) saw
              nothing under any heading, and the answers existed only in the
              FAQPage JSON-LD. Open by default, each answer ships as a <p>
              directly beneath its <h3>; visitors can still collapse them. */}
          <Accordion type="multiple" defaultValue={FAQS.map((_, i) => `faq-${i}`)} className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b hairline">
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                  <p>{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-32 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">Begin</div>
          <h2 className="font-serif text-4xl md:text-6xl mb-6 tracking-tight max-w-3xl mx-auto leading-[1.05]">
            Your next piece starts with a single brief.
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
            Free to submit, transparent to price, and delivered by specialists who never sign their name.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/categories" className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Explore categories <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
            </Link>
            <Link to="/how-it-works" className="inline-flex items-center gap-3 border hairline px-8 py-4 text-xs uppercase tracking-widest hover:border-primary/60 transition-colors duration-300">
              How it works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
